<template>
  <section class="cohort-form-class-item-component">
    <header class="class-header">
      <div class="technical-name">{{ clazz.technicalName }}</div>
      <i v-if="canDeleteClass" class="pi pi-trash" @click="onDelete" />
    </header>
    <section class="attributes-list">
      <el-table :data="clazz.attributes" stripe style="width: 100%" class="attributes-table single-instance">
        <el-table-column fixed prop="displayName" label="Display name" min-width="200px" show-overflow-tooltip />
        <el-table-column fixed prop="technicalName" label="Technical name" min-width="200px" />
        <el-table-column fixed prop="type" label="Type" width="100px" />
        <el-table-column
          :label="clazz.instances.length > 1 ? 'Instance 1 values' : 'Values'"
          show-overflow-tooltip
          align="left"
          prop="value"
          min-width="200px"
        >
          <template #default="scope">
            <div
              :class="{
                overridden: overriddenValues?.[clazz.instances[0].id]?.[scope.row.technicalName]?.isOverridden,
              }"
            >
              <VersatileFormField
                :prop="scope.row.technicalName"
                :type="scope.row.formFieldType"
                :model-value="getValueForInstance(clazz.instances[0].id, scope.row.technicalName)"
                :choices="scope.row.availableValues && scope.row.availableValues.map((o:unknown) => ({ label: o, value: o }))"
                :auto-size="{ minRows: 1, maxRows: 6 }"
                inline
                @update:model-value="
                  (value) =>
                    updateAttributeValue({
                      instanceId: clazz.instances[0].id,
                      attrTechnicalName: scope.row.technicalName,
                      value,
                    })
                "
              />
            </div>
          </template>
        </el-table-column>
      </el-table>

      <div v-if="clazz.instances.length > 1" class="drawer-wrapper" @click="toggleDrawer">
        <VdIcon store="fa" :name="isDrawerOpen ? 'chevron-down' : 'chevron-right'" size="large" />
        <span>Multi instance view</span>
      </div>

      <el-table
        v-if="isDrawerOpen && clazz.instances.length > 1"
        :data="instanceIds(clazz.dynamicInstances)"
        stripe
        class="attributes-table"
      >
        <el-table-column prop="" fixed label="Attribute name" width="220px">
          <template #default="scope">
            <span class="instance">Instance {{ scope.$index + 1 }}</span>
            <span v-if="clazz.dynamicInstances" @click="onDeleteInstance(scope.row)">
              <i class="pi pi-trash" />
            </span>
          </template>
        </el-table-column>

        <el-table-column
          v-for="attribute in clazz.attributes"
          :key="attribute.technicalName"
          :label="attribute.displayName"
          show-overflow-tooltip
          align="left"
          prop="value"
          min-width="200px"
        >
          <template #header>
            <div>
              {{ attribute.displayName }}
            </div>
            <div class="class-attribute-technical-name">
              {{ attribute.technicalName }}
            </div>
          </template>
          <template #default="scope">
            <div :class="{ overridden: overriddenValues?.[scope.row]?.[attribute.technicalName]?.isOverridden }">
              <VersatileFormField
                :prop="attribute.technicalName"
                :type="attribute.formFieldType"
                :model-value="getValueForInstance(scope.row, attribute.technicalName)"
                :choices="attribute.availableValues && attribute.availableValues.map((o:unknown) => ({ label: o, value: o }))"
                :auto-size="{ minRows: 1, maxRows: 6 }"
                inline
                @update:model-value="
                  (value) =>
                    updateAttributeValue({
                      instanceId: scope.row,
                      attrTechnicalName: attribute.technicalName,
                      value,
                    })
                "
              />
            </div>
          </template>
        </el-table-column>
      </el-table>
    </section>
  </section>
</template>

<script lang="ts">
import throttle from "lodash.throttle";
import { defineComponent, PropType, reactive, ref, watch } from "vue";

import VersatileFormField from "@/components/structure/VersatileFormField/VersatileFormField.vue";
import { AttributeValue, CohortFormPageClassFragment, CohortInputInstance, CohortInputOverride } from "@/graphql";
import { objectifyArray } from "@/utils";

export default defineComponent({
  name: "CohortFormClassItem",

  components: {
    VersatileFormField,
  },

  props: {
    /**
     * If the cohortId is present, update the cohort, create it otherwise.
     */
    cohortId: {
      type: String,
      required: false,
      default: null,
    },

    clazz: {
      type: Object as PropType<CohortFormPageClassFragment>,
      required: true,
    },

    overrideInstances: {
      type: Array<CohortInputInstance>,
      required: true
    },

    modelValue: {
      type: Object as PropType<CohortInputOverride>,
      default: () => null,
    },

    canDeleteClass: {
      type: Boolean,
      default: true,
    },

    overriddenValues: {
      type: Object as PropType<Record<string, Record<string, { isOverridden?: boolean }>>>,
      default: () => ({}),
    },
  },

  emits: ["update:modelValue", "delete"],

  setup(props, { emit }) {
    const form = reactive<CohortInputOverride>({
      classId: props.clazz.technicalName,
      instances: [],
    });

    const instanceIds = (dynamicInstances: boolean) => {
      const classInstanceIds = props.clazz.instances
        ?.map(({ id }) => id) ?? [];

      const overriddeOnlyInstanceIds = props.overrideInstances
        ?.map(({ id }) => id) ?? [];

      return dynamicInstances ? overriddeOnlyInstanceIds : classInstanceIds;
    }

    /**
     * Fill the form with the value passed in props
     */
    watch(
      () => props.modelValue,
      (override) => {
        if (override) {
          form.instances = override.instances ? override.instances : [];
        }
      },
      {
        immediate: true,
      }
    );

    /**
     * Combine the overridden values and the default values to
     * provide all the attribute values.
     */

    const getValueForInstance = (instanceId: string, attrTechnicalName: string) => {
      const defaultValuesByInstance = objectifyArray(props.clazz.instances, [
        "id",
        "values",
        ["technicalName", "value"],
      ]);
      const overriddenValuesByInstance = objectifyArray(form.instances, ["id", "values", ["technicalName", "value"]]);

      const defaultValue = defaultValuesByInstance[instanceId]?.[attrTechnicalName];

      const instanceValues = overriddenValuesByInstance[instanceId];
      if (instanceValues) {
        return instanceValues[attrTechnicalName] ?? defaultValue;
      }
      return defaultValue;
    };

    /**
     * Handle an attribute value update
     */
    type UpdateAttributeValueArgs = { instanceId: string; attrTechnicalName: string; value: AttributeValue };

    const updateAttributeValue = throttle(({ instanceId, attrTechnicalName, value }: UpdateAttributeValueArgs) => {
      form.instances = form.instances.map((instance) => {
        if (instance.id === instanceId) {
          return {
            ...instance,
            values: instance.values.map((attrValue) => {
              if (attrValue.technicalName === attrTechnicalName) {
                return { technicalName: attrTechnicalName, value };
              }
              return attrValue;
            }),
          };
        }
        return instance;
      });
      emit("update:modelValue", { ...form });
    }, 500);

    const onDeleteInstance = async (instanceId: string) => {
      form.instances = form.instances.filter((({id}) => id !== instanceId))

      emit("update:modelValue", { ...form });
    };

    /**
     * Trigger delete when removing a class
     */
    const onDelete = () => {
      emit("delete");
    };

    const isDrawerOpen = ref(false);

const toggleDrawer = () => {
  isDrawerOpen.value = !isDrawerOpen.value;
};

    return {
      form,
      getValueForInstance,
      updateAttributeValue,
      onDelete,
      instanceIds,
      onDeleteInstance,
      isDrawerOpen,
      toggleDrawer,
    };
  },
});
</script>

<style lang="scss">
.cohort-form-class-item-component {
  color: var(--vd-color-text-default);

  .attributes-table {
    &.single-instance {
      &.el-table {
        margin-top: -2px !important;
      }

      .el-table__body td.el-table__cell:first-child {
        background: white !important;
        border-right: none;
      }
    }
  }

  .el-table {
    color: var(--vd-color-text-default);
    border: 1px solid var(--vd-color-text-light-7);
    border-bottom: 1px;
    border-top: none;

    th.el-table__cell.is-leaf {
      background: var(--vd-color-text-light-9) !important;
    }

    .el-table__body td.el-table__cell:first-child {
      background: var(--vd-color-text-light-9) !important;
      border-right: 1px solid var(--vd-color-text-light-7);
      font-weight: 700;
    }
  }

  .el-table thead {
    color: var(--vd-color-text-default);
    font-weight: 700;

    .class-attribute-technical-name {
      font-size: font("S");
      color: var(--vd-color-text-light-2);
      margin-top: -4px;
      font-weight: 500;
    }
  }

  .pi-trash {
    cursor: pointer;
    color: var(--vd-color-blue-default);
    margin-left: 0.5rem;
  }

  header.class-header {
    display: flex;
    align-items: center;
    padding: 1rem;
    font-weight: 700;
    background-color: var(--vd-color-text-light-9);
    border: 1px solid var(--vd-color-text-light-7);
  }

  .p-dropdown-panel.p-component.p-ripple-disabled {
    z-index: 100000 !important;
  }

  .drawer-wrapper {
    padding: 1rem 0;
    cursor: pointer;
    border-bottom: 1px solid var(--vd-color-text-light-7);

    svg {
      margin-right: 0.5rem;
      font-size: 12px;
    }

    span {
      margin-right: 0.5rem;
      font-weight: 700;
      color: var(--vd-color-text-default);
    }
  }

  .overridden {
    border-radius: var(--el-border-radius-base);
    background-color: $cloudy-blue;

    input[type="text"]:not([role="combobox"]),
    input[type="integer"] {
      background-color: $cloudy-blue;
    }
  }
}
</style>
